<template>
  <!-- Contenedor único para todo el contenido -->
  <div class="w-full">
    <!-- Contenedor blanco de la cabecera -->
    <div class="bg-white p-4 h-[68px] rounded-lg shadow-md">
      <div class="flex flex-wrap h-full">
        <div class="flex flex-1 items-center">
          <!-- Título principal -->
          <fwb-heading tag="h2" class="text-style-xl">
            Códigos telefónicos internacionales
          </fwb-heading>
        </div>
      </div>
    </div>

    <!-- Descripción -->
    

    <!-- Contenedor para el contenido principal -->
    <div class="mx-auto p-4 px-0">
      <div class="flex flex-wrap">
        <div class="w-full rounded overflow-hidden shadow-lg bg-white p-6">
          <div class="mx-auto mt-4">
            <p class="text-gray-600">Selecciona las ladas que deseas mostrar en el selector de códigos telefónicos</p>
          </div>
          <!-- Barra de búsqueda y acciones - Diseño responsivo -->
          <div class="filters-container flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-4">
            <!-- Buscador - Ocupa todo el espacio disponible -->
            <div class="w-full md:flex-1">
              <input 
                @input="(e) => debouncedSearch(e.target.value)"
                @keyup.enter="(e) => { 
                  if (!e.target.value.trim()) searchTerm.value = ''; 
                }"
                :value="searchTerm"
                type="text" 
                placeholder="Buscar país" 
                class="w-full search-input border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            
            <!-- Contenedor de botones -->
            <div class="w-full md:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button @click="selectAll" class="w-full sm:w-auto custom-action-btn">Seleccionar todos</button>
              <button @click="deselectAll" class="w-full sm:w-auto custom-action-btn">Deseleccionar todos</button>
              <button @click="saveChanges" class="w-full sm:w-auto custom-create-btn flex items-center justify-center">Guardar cambios</button>
            </div>
          </div>

          <!-- Lista de países -->
          <div class="countries-container">
            <div v-if="loading" class="loading py-4 text-center text-gray-500">Buscador de país</div>
            <div v-else-if="filteredCountries.length === 0" class="no-results py-4 text-center text-gray-500">
              No se encontraron países que coincidan con la búsqueda
            </div>
            <div 
              v-for="country in filteredCountries" 
              :key="country.dial_code" 
              class="country-item p-3 hover:bg-gray-50 rounded flex items-center"
            >
              <div class="country-info flex items-center gap-3 w-full">
                <!-- Checkbox al lado de la bandera -->
                <label class="checkbox-container flex items-center">
                  <input 
                    type="checkbox" 
                    v-model="country.active" 
                    :id="`country-${country.dial_code}`" 
                    class="hidden"
                  />
                  <span class="checkmark mr-2"></span>
                </label>
                
                <img 
                  v-if="country.flag" 
                  :src="country.flag" 
                  :alt="`Bandera de ${country.name}`" 
                  class="flag-img w-6 h-auto border border-gray-200"
                />
                <span class="country-name flex-grow">{{ country.name }}</span>
                <span class="country-code text-gray-500 text-sm">{{ country.dial_code }}</span>
              </div>
            </div>
          </div>

          <!-- Mensaje y contador -->
          <div class="mt-4">
            <div v-if="message" :class="['message', messageType, 'p-3', 'rounded']">{{ message }}</div>
            <div class="counter text-right text-gray-500 text-sm mt-2">
              {{ selectedCount }} países seleccionados de {{ countryCodes.length }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import countries from 'i18n-iso-countries';
import { debounce } from 'lodash';

const store = useStore();
const session = computed(() => store.getters.getUserSession);

const countryCodes = ref([]);
const loading = ref(true);
const searchTerm = ref("");
const message = ref("");
const messageType = ref("success");

  const debouncedSearch = debounce((term) => {
    searchTerm.value = term.trim(); 
  }, 300);

// Filtro para países basado en término de búsqueda
const filteredCountries = computed(() => {
  // Hacer una copia del array original
  const countriesCopy = [...countryCodes.value];
  
  // Ordenar alfabéticamente
  countriesCopy.sort((a, b) => a.name.localeCompare(b.name));
  
  // Si no hay término de búsqueda o está vacío después de trim, devolver todos los países
  if (!searchTerm.value || !searchTerm.value.trim()) {
    return countriesCopy;
  }
  
  const search = searchTerm.value.toLowerCase().trim(); // Añadimos trim()
  const searchWithoutAccents = removeAccents(search);
  
  return countriesCopy.filter(country => {
    const countryName = country.name.toLowerCase();
    const countryNameWithoutAccents = removeAccents(country.name);
    const countryCode = country.dial_code.toLowerCase();
    
    // Primero verificar coincidencia exacta al principio del nombre
    if (countryName.startsWith(search) || 
        countryNameWithoutAccents.startsWith(searchWithoutAccents)) {
      return true;
    }
    
    // Luego verificar coincidencia exacta al principio de cualquier palabra del nombre
    const wordsInName = countryName.split(' ');
    const wordsInNameWithoutAccents = countryNameWithoutAccents.split(' ');
    
    if (wordsInName.some(word => word.startsWith(search)) ||
        wordsInNameWithoutAccents.some(word => word.startsWith(searchWithoutAccents))) {
      return true;
    }
    
    // Finalmente verificar coincidencia con el código de país
    return countryCode.startsWith(search);
  });
});

const removeAccents = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

// Contador de países seleccionados
const selectedCount = computed(() => {
  return countryCodes.value.filter(country => country.active).length;
});

// Cargar países con estado activo/inactivo
onMounted(async () => {
  await loadCountryCodes();
});
// Registra las traducciones al español
countries.registerLocale(require('i18n-iso-countries/langs/es.json'));


const loadCountryCodes = async () => {
  loading.value = true;
  const token = session.value ? session.value.token_api : null;

  if (!token) {
    message.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    messageType.value = "error";
    loading.value = false;
    return;
  }

  const url = `${process.env.VUE_APP_API_BASE_URL}/country-codes`;
  const options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.trim()}`,
    },
  };

  try {
    const response = await axios.get(url, options);
    
    // Traducir y ordenar los países
    countryCodes.value = response.data
      .map(country => ({
        ...country,
        name: countries.getName(country.code, 'es') || country.name
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    console.log("Países cargados y traducidos:", countryCodes.value);
    loading.value = false;
  } catch (err) {
    console.error("Error al cargar códigos de país:", err);
    message.value = "No se pudieron cargar los códigos de país.";
    messageType.value = "error";
    loading.value = false;
  }
};

// Guardar cambios - modifica la función saveChanges
const saveChanges = async () => {
  const activeDialCodes = countryCodes.value
    .filter(country => country.active)
    .map(country => country.dial_code);
  
  const token = session.value ? session.value.token_api : null;
  
  if (!token) {
    showMessage("No se encontró la sesión del usuario", "error");
    return;
  }
  
  try {
    const url = `${process.env.VUE_APP_API_BASE_URL}/save-country-preferences`;
    await axios.post(
      url,
      { active_countries: activeDialCodes },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.trim()}`,
        }
      }
    );

    showMessage("Configuración guardada correctamente", "success");
  } catch (e) {
    console.error("Error al guardar configuración:", e);
    showMessage("Error al guardar la configuración", "error");
  }
};
// Seleccionar todos los países
const selectAll = () => {
  countryCodes.value.forEach(country => {
    country.active = true;
  });
  showMessage("Se han seleccionado todos los países", "success");
};

// Deseleccionar todos los países
const deselectAll = () => {
  countryCodes.value.forEach(country => {
    country.active = false;
  });
  showMessage("Se han deseleccionado todos los países", "success");
};


// Mostrar mensaje temporal
const showMessage = (msg, type = "success") => {
  message.value = msg;
  messageType.value = type;
  
  // Limpiar mensaje después de 3 segundos
  setTimeout(() => {
    message.value = "";
  }, 3000);
};
</script>

<style scoped>
.container {
  font-family: 'Poppins', sans-serif;
}

.text-style-xl {
  font-size: 1rem;
  font-weight: 600;
  color: #4A4A4A;
}

.countries-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
}

.checkbox-container {
  position: relative;
  cursor: pointer;
  user-select: none;
  margin-right: 12px;
}

.checkmark {
  position: relative;
  height: 20px;
  width: 20px;
  background-color: #eee;
  border-radius: 4px;
  transition: all 0.2s;
}

.checkbox-container:hover input ~ .checkmark {
  background-color: #ddd;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #FF4265;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.custom-create-btn {
  padding: 0.5rem 1rem;
  background-color: #FF4265;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  transition: background-color 0.3s ease;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.custom-create-btn:hover {
  background-color: #e03a5f;
}

.custom-action-btn {
  padding: 0.5rem 1rem;
  background-color: #f0f0f0;
  color: #4A4A4A;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.custom-action-btn:hover {
  background-color: #e0e0e0;
}

.message {
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 14px;
}

.success {
  background-color: #d1e7dd;
  color: #1c5f48;
}

.error {
  background-color: #f8d7da;
  color: #842029;
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .filters-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .filters-group {
    width: 100%;
  }

  .search-input {
    width: 100%;
  }
  
  .countries-container {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 640px) {
  .filters-group {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

@media (min-width: 768px) {
  .filters-container {
    flex-direction: row;
    justify-content: space-between;
  }
}

@media (min-width: 1024px) {
  .filters-group {
    flex-wrap: nowrap;
  }
}
</style>
